import React, { useState, useEffect } from 'react';
import FormSection from '../FormSection';
import StandsGrid from '../StandsGrid';
import SelectedStands from '../SelectedStands';
import ConfirmedStands from '../ConfirmedStands';
import { SelectedStand } from '../../types/stand.types';
import '../CheckboxGroup.css';
interface ExpoZoneSectionProps {
  selectedStands?: string[];
  confirmedStands?: string[];
  onChange: (stands: string[]) => void;
  onConfirmedChange?: (stands: string[]) => void;
  readOnly?: boolean;
  requestInformation?: boolean;
  onRequestInformationChange?: (value: boolean) => void;
  hideTitle?: boolean;
  hideLegend?: boolean;
  hideCheckbox?: boolean;
  customTitle?: string;
  /** Cuando true, la sección se usa en el modal "Crear contacto" del CRM; los cambios solo aplican aquí y no en el perfil privado */
  createContactMode?: boolean;
}
const ExpoZoneSection: React.FC<ExpoZoneSectionProps> = ({ selectedStands = [], confirmedStands = [], onChange, onConfirmedChange, readOnly = false, requestInformation = false, onRequestInformationChange, hideTitle = false, hideLegend = false, hideCheckbox = false, customTitle, createContactMode = false }) => {
  const [selectedStandsData, setSelectedStandsData] = useState<SelectedStand[]>([]);
  const [confirmedStandsData, setConfirmedStandsData] = useState<SelectedStand[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Log para diagnóstico
  useEffect(() => {
  }, [selectedStands, selectedStandsData]);
  // Función para obtener el nombre descriptivo del stand (convierte ID antiguo a nombre nuevo)
  const getStandDisplayName = (standId: string): string => {
    switch (standId) {
      case 'plaza-auto1':
        return 'C1';
      case 'plaza-auto2':
        return 'C11';
      case 'plaza-innovacion':
      case 'plaza-innovacion1':
        return 'C21';
      case 'exA':
        return 'Stand Exterior A';
      case 'exB':
        return 'Stand Exterior B';
      case 'exC':
        return 'Stand Exterior C';
      case 'stand-c1-1': return 'C2';
      case 'stand-c1-2': return 'C3';
      case 'stand-c1-3': return 'C6';
      case 'stand-c1-4': return 'C7';
      case 'stand-c1-5': return 'C4';
      case 'stand-c1-6': return 'C5';
      case 'stand-c1-7': return 'C8';
      case 'stand-c1-8': return 'C9';
      case 'stand-c1-alto': return 'C10';
      case 'stand-c21-1': return 'C22';
      case 'stand-c21-2': return 'C23';
      case 'stand-c21-c1-1': return 'C12';
      case 'stand-c21-c1-2': return 'C13';
      case 'stand-c21-c1-3': return 'C16';
      case 'stand-c21-c1-4': return 'C17';
      case 'stand-c21-c1-5': return 'C14';
      case 'stand-c21-c1-6': return 'C15';
      case 'stand-c21-c1-7': return 'C18';
      case 'stand-c21-c1-8': return 'C19';
      case 'stand-c21-alto': return 'C20';
      default:
        // Si el standId es un número, verificar si es P1-P20
        if (standId === '1') return 'P1';
        if (standId === '2') return 'P2';
        if (standId === '3') return 'P3';
        if (standId === '4') return 'P4';
        if (standId === '5') return 'P5';
        if (standId === '6') return 'P6';
        if (standId === '7') return 'P7';
        if (standId === '8') return 'P8';
        if (standId === '9') return 'P9';
        if (standId === '10') return 'P10';
        if (standId === '35') return 'P11';
        if (standId === '36') return 'P12';
        if (standId === '37') return 'P13';
        if (standId === '38') return 'P14';
        if (standId === '39') return 'P15';
        if (standId === '40') return 'P16';
        if (standId === '41') return 'P17';
        if (standId === '42') return 'P18';
        if (standId === '43') return 'P19';
        if (standId === '44') return 'P20';
        // Si ya es un nombre nuevo (C1, C2, P1, etc.), devolverlo tal cual
        return standId;
    }
  };
  
  // Función para obtener el tamaño del stand basándose en el nombre nuevo
  const getStandSizeByName = (standName: string): string => {
    // Stands naranjas (P1-P20) - 5m²
    if (standName.startsWith('P') && ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10', 'P11', 'P12', 'P13', 'P14', 'P15', 'P16', 'P17', 'P18', 'P19', 'P20'].includes(standName)) {
      return '5m²';
    }
    // Stands verde-agua (C2-C9, C12-C19) - 10m²
    if (['C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C12', 'C13', 'C14', 'C15', 'C16', 'C17', 'C18', 'C19'].includes(standName)) {
      return '10m²';
    }
    // Stands azules (C10, C20) - 20m²
    if (['C10', 'C20'].includes(standName)) {
      return '20m²';
    }
    // Plazas automotrices (C1, C11) - 40m²
    if (['C1', 'C11'].includes(standName)) {
      return '40m²';
    }
    // Plaza de la innovación (C21) - 38.5m²
    if (standName === 'C21') {
      return '38.5m²';
    }
    // Stands celestes
    if (standName === 'C22') {
      return '40m²';
    }
    if (standName === 'C23') {
      return '60m²';
    }
    // Stands exteriores - sin medida específica
    if (standName.startsWith('Stand Exterior')) {
      return '';
    }
    return '5m²'; // Tamaño por defecto
  };
  // Función para obtener el tamaño real del stand
  const getStandSize = (standId: string): string => {
    switch (standId) {
      // Stands naranjas (P1-P10, P11-P20) - 5m²
      case '1': case '2': case '3': case '4': case '5': case '6': case '7': case '8': case '9': case '10':
      case '35': case '36': case '37': case '38': case '39': case '40': case '41': case '42': case '43': case '44':
        return '5m²';
      // Stands verde-agua (C2-C9, C12-C19) - 10m²
      case 'stand-c1-1': case 'stand-c1-2': case 'stand-c1-3': case 'stand-c1-4':
      case 'stand-c1-5': case 'stand-c1-6': case 'stand-c1-7': case 'stand-c1-8':
      case 'stand-c21-c1-1': case 'stand-c21-c1-2': case 'stand-c21-c1-3': case 'stand-c21-c1-4':
      case 'stand-c21-c1-5': case 'stand-c21-c1-6': case 'stand-c21-c1-7': case 'stand-c21-c1-8':
        return '10m²';
      // Stands azules (C10, C20) - 20m²
      case 'stand-c1-alto': case 'stand-c21-alto':
        return '20m²';
      // Plazas automotrices (C1, C11) - 40m²
      case 'plaza-auto1': case 'plaza-auto2':
        return '40m²';
      // Plaza de la innovación (C21) - 38.5m²
      case 'plaza-innovacion': case 'plaza-innovacion1':
        return '38.5m²';
      // Stands celestes (C22, C23)
      case 'stand-c21-1':
        return '40m²';
      case 'stand-c21-2':
        return '60m²';
      // Stands exteriores - sin medida específica
      case 'exA': case 'exB': case 'exC':
        return '';
      default:
        return '5m²'; // Tamaño por defecto
    }
  };
  // Sincronizar selectedStands con selectedStandsData
  useEffect(() => {
    try {
      // CRÍTICO: Asegurar que selectedStands sea siempre un array
      let standsArray: string[] = [];
      if (Array.isArray(selectedStands)) {
        standsArray = selectedStands;
      } else if (selectedStands && typeof selectedStands === 'string') {
        // Si es un string, intentar parsearlo como JSON
        try {
          const parsed = JSON.parse(selectedStands);
          standsArray = Array.isArray(parsed) ? parsed : [];
        } catch {
          // Si no es JSON válido, tratarlo como un solo stand
          standsArray = [selectedStands];
        }
      } else if (selectedStands) {
        // Si es otro tipo de objeto, intentar convertirlo
        standsArray = [];
      }
      // Convertir IDs antiguos a nombres nuevos y normalizar
      const normalizedStands = standsArray.map(standId => {
        const standIdStr = String(standId);
        // Si ya es un nombre nuevo (C1, C2, P1, etc.), usarlo directamente
        // Si es un ID antiguo, convertirlo al nombre nuevo
        return getStandDisplayName(standIdStr);
      });
      
      // Actualizar selectedStands con los nombres nuevos si hay diferencias
      const hasOldIds = standsArray.some(id => {
        const idStr = String(id);
        const newName = getStandDisplayName(idStr);
        return idStr !== newName;
      });
      
      if (hasOldIds) {
        // Si hay IDs antiguos, actualizar con los nombres nuevos
        onChange(normalizedStands);
      }
      
      const standsData: SelectedStand[] = normalizedStands.map(standName => ({
        id: standName, // Usar el nombre nuevo como ID
        name: standName,
        size: getStandSizeByName(standName)
      }));
      setSelectedStandsData(standsData);
      setError(null);
    } catch (err) {
      setError('Error al cargar los stands seleccionados');
      setSelectedStandsData([]); // Resetear a array vacío en caso de error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStands]);
  // Sincronizar confirmedStands con confirmedStandsData
  useEffect(() => {
    try {
      // CRÍTICO: Asegurar que confirmedStands sea siempre un array
      let standsArray: string[] = [];
      if (Array.isArray(confirmedStands)) {
        standsArray = confirmedStands;
      } else if (confirmedStands && typeof confirmedStands === 'string') {
        // Si es un string, intentar parsearlo como JSON
        try {
          const parsed = JSON.parse(confirmedStands);
          standsArray = Array.isArray(parsed) ? parsed : [];
        } catch {
          // Si no es JSON válido, tratarlo como un solo stand
          standsArray = [confirmedStands];
        }
      } else if (confirmedStands) {
        // Si es otro tipo de objeto, intentar convertirlo
        standsArray = [];
      }
      // Convertir IDs antiguos a nombres nuevos
      const normalizedStands = standsArray.map(standId => {
        const standIdStr = String(standId);
        return getStandDisplayName(standIdStr);
      });
      
      const standsData: SelectedStand[] = normalizedStands.map(standName => ({
        id: standName,
        name: standName,
        size: getStandSizeByName(standName)
      }));
      setConfirmedStandsData(standsData);
      setError(null);
    } catch (err) {
      setError('Error al cargar los stands confirmados');
      setConfirmedStandsData([]); // Resetear a array vacío en caso de error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedStands]);
  const handleStandToggle = (standId: string) => {
    if (readOnly) return;
    // Si hay onConfirmedChange, estamos en modo admin: el clic normal selecciona stands confirmados
    if (onConfirmedChange) {
      const newStandName = getStandDisplayName(standId);
      const isAlreadyConfirmed = confirmedStands.includes(standId) || confirmedStands.includes(newStandName);
      const newConfirmedStands = isAlreadyConfirmed
        ? confirmedStands.filter(id => id !== standId && id !== newStandName)
        : [...confirmedStands.filter(id => id !== standId && id !== newStandName), newStandName];
      onConfirmedChange(newConfirmedStands);
    } else {
      // Modo normal: seleccionar stands seleccionados
      const newStandName = getStandDisplayName(standId);
      const isAlreadySelected = selectedStands.includes(standId) || selectedStands.includes(newStandName);
      const newSelectedStands = isAlreadySelected
        ? selectedStands.filter(id => id !== standId && id !== newStandName)
        : [...selectedStands.filter(id => id !== standId && id !== newStandName), newStandName];
    onChange(newSelectedStands);
    }
  };
  const handleRemoveStand = (standId: string) => {
    if (readOnly) return;
    // Convertir el ID antiguo al nombre nuevo si es necesario
    const newStandName = getStandDisplayName(standId);
    // Remover tanto el ID antiguo como el nombre nuevo por si acaso
    const newSelectedStands = selectedStands.filter(id => id !== standId && id !== newStandName);
    onChange(newSelectedStands);
  };
  
  const handleRemoveConfirmedStand = (standId: string) => {
    if (readOnly || !onConfirmedChange) return;
    // Convertir el ID antiguo al nombre nuevo si es necesario
    const newStandName = getStandDisplayName(standId);
    // Remover tanto el ID antiguo como el nombre nuevo por si acaso
    const newConfirmedStands = confirmedStands.filter(id => id !== standId && id !== newStandName);
    onConfirmedChange(newConfirmedStands);
  };
  // Si hay error, mostrarlo
  if (error) {
    return (
      <FormSection 
        title={createContactMode ? undefined : (
          <>
            Espacios para stands en Zona Expo interior y exterior{' '}
            <span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b' }}>(La disposición de los espacios podrá ser modificada por la organización)</span>
          </>
        )}
        description={createContactMode ? undefined : "En caso de aún no haber confirmado tu espacio, indica los que son de tu interés para recibir más información"}
      >
        <div className="expo-zone-section" style={{ padding: '20px', color: '#CE2B15' }}>
          <p>Error: {error}</p>
          <button onClick={() => setError(null)}>Reintentar</button>
        </div>
      </FormSection>
    );
  }
  try {
    // Si hay customTitle y hideTitle es false, renderizar con estilo personalizado
    if (customTitle && !hideTitle) {
      return (
        <div className="expo-zone-section" style={{ marginTop: '0' }}>
          {!createContactMode && (
          <span className="info-label" style={{ display: 'block', marginBottom: '12px' }}>{customTitle}</span>
          )}
          <div className="expo-zone-section">
          {/* Mapa de Stands */}
          <div className="stands-section">
            <StandsGrid
              selectedStands={selectedStands}
              confirmedStands={confirmedStands}
              onStandToggle={handleStandToggle}
              onConfirmedToggle={onConfirmedChange ? handleStandToggle : undefined}
              readOnly={readOnly}
            />
          </div>
        {/* Leyenda - oculta en modal crear contacto */}
        {!hideLegend && !createContactMode && (
          <div className="stands-legend-container">
            <div className="stands-legend">
              <span className="legend-item">
                <span className="legend-color" style={{background:'#F8981D'}}></span>
                STANDS 5m²
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{background:'#26A69A'}}></span>
                STANDS 10m²
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{background:'#1E88E5'}}></span>
                STANDS 20m²
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{background:'#139B47B3'}}></span>
                STANDS 40m²
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{background:'#0D7A3D'}}></span>
                STANDS 38.5m²
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{background:'#4BE8F9'}}></span>
                STANDS 40m²
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{background:'#2BC4D9'}}></span>
                STANDS 60m²
              </span>
            </div>
          </div>
        )}
        {/* Checkbox: Solicitar envío de información - oculto en modal crear contacto */}
        {!hideCheckbox && !createContactMode && (
          <div className="stands-legend-container" style={{ marginTop: '4px', marginBottom: '24px' }}>
            <div className="checkbox-group">
              <label className="checkbox-item" style={{ alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={requestInformation || false}
                  onChange={(e) => {
                    if (onRequestInformationChange && !readOnly) {
                      onRequestInformationChange(e.target.checked);
                    }
                  }}
                  disabled={readOnly}
                />
                <span className="checkbox-label" style={{ marginTop: '2px' }}>Solicitar envío de información</span>
              </label>
            </div>
          </div>
        )}
        {/* Stands Seleccionados y Confirmados */}
        <div className="stands-column-container">
          <SelectedStands
            selectedStands={selectedStandsData}
            onRemoveStand={handleRemoveStand}
            readOnly={readOnly}
          />
          <ConfirmedStands
            confirmedStands={confirmedStandsData}
            onRemoveStand={onConfirmedChange ? handleRemoveConfirmedStand : undefined}
            readOnly={readOnly || !onConfirmedChange}
          />
        </div>
          </div>
        </div>
      );
    }
    
    // Renderizado normal con FormSection
    return (
      <FormSection 
        title={(hideTitle || createContactMode) ? undefined : (customTitle || (
          <>
            Espacios para stands en Zona Expo interior y exterior{' '}
            <span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b' }}>(La disposición de los espacios podrá ser modificada por la organización)</span>
          </>
        ))}
        description={(hideTitle || createContactMode) ? undefined : "En caso de aún no haber confirmado tu espacio, indica los que son de tu interés para recibir más información"}
      >
        <div className="expo-zone-section">
          {/* Mapa de Stands */}
          <div className="stands-section">
            <StandsGrid
              selectedStands={selectedStands}
              confirmedStands={confirmedStands}
              onStandToggle={handleStandToggle}
              onConfirmedToggle={onConfirmedChange ? handleStandToggle : undefined}
              readOnly={readOnly}
            />
          </div>
        {/* Leyenda - oculta en modal crear contacto */}
        {!hideLegend && !createContactMode && (
        <div className="stands-legend-container">
          <div className="stands-legend">
            <span className="legend-item">
              <span className="legend-color" style={{background:'#F8981D'}}></span>
              STANDS 5m²
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background:'#26A69A'}}></span>
              STANDS 10m²
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background:'#1E88E5'}}></span>
              STANDS 20m²
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background:'#139B47B3'}}></span>
              STANDS 40m²
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background:'#0D7A3D'}}></span>
              STANDS 38.5m²
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background:'#4BE8F9'}}></span>
              STANDS 40m²
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{background:'#2BC4D9'}}></span>
              STANDS 60m²
            </span>
          </div>
        </div>
        )}
        {/* Checkbox: Solicitar envío de información - oculto en modal crear contacto */}
        {!hideCheckbox && !createContactMode && (
        <div className="stands-legend-container" style={{ marginTop: '4px', marginBottom: '24px' }}>
          <div className="checkbox-group">
            <label className="checkbox-item" style={{ alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={requestInformation || false}
                onChange={(e) => {
                  if (onRequestInformationChange && !readOnly) {
                    onRequestInformationChange(e.target.checked);
                  }
                }}
                disabled={readOnly}
              />
              <span className="checkbox-label" style={{ marginTop: '2px' }}>Solicitar envío de información</span>
            </label>
          </div>
        </div>
        )}
        {/* Stands Seleccionados y Confirmados */}
        <div className="stands-column-container">
          <SelectedStands
            selectedStands={selectedStandsData}
            onRemoveStand={handleRemoveStand}
            readOnly={readOnly}
          />
          <ConfirmedStands
            confirmedStands={confirmedStandsData}
            onRemoveStand={onConfirmedChange ? handleRemoveConfirmedStand : undefined}
            readOnly={readOnly || !onConfirmedChange}
          />
        </div>
      </div>
    </FormSection>
    );
  } catch (err) {
    return (
      <FormSection 
        title={(hideTitle || createContactMode) ? undefined : (customTitle || (
          <>
            Espacios para stands en Zona Expo interior y exterior{' '}
            <span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b' }}>(La disposición de los espacios podrá ser modificada por la organización)</span>
          </>
        ))}
        description={(hideTitle || createContactMode) ? undefined : "En caso de aún no haber confirmado tu espacio, indica los que son de tu interés para recibir más información"}
      >
        <div className="expo-zone-section" style={{ padding: '20px', color: '#CE2B15' }}>
          <p>Error al cargar la sección de Zona Expo. Por favor, recarga la página.</p>
          <p style={{ fontSize: '12px', marginTop: '10px' }}>Error: {err instanceof Error ? err.message : 'Error desconocido'}</p>
        </div>
      </FormSection>
    );
  }
};
export default ExpoZoneSection;
